import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Avatar } from './avatar.js';

export type Zone = 'app' | 'admin' | 'platform';

export interface NavItem {
  key: string;
  label: string;
  href: string;
  icon?: ReactNode;
  count?: number;
}

export interface NavSection {
  label?: string;
  items: NavItem[];
}

export interface SidebarProps {
  brand: string;
  organisation?: string;
  /**
   * Het logo van de klant naast zijn naam (Extra 1).
   *
   * Voor een donkere achtergrond: de zijbalk is donkerblauw. Ontbreekt het, dan staat er alleen
   * de naam - geen vlak met initialen, want in een beheerportaal weet de gebruiker bij welk
   * bedrijf hij werkt.
   */
  logoUrl?: string | null;
  sections: NavSection[];
  activeKey?: string;
  /** Wordt als <a>-wrapper gebruikt zodat de router van de app de navigatie afhandelt. */
  renderLink: (item: NavItem, className: string, children: ReactNode) => ReactNode;
  footer?: ReactNode;
  /**
   * Een omhulling rond logo en naam, bv. een menu om van bedrijf te wisselen. De zijbalk weet
   * niets van bedrijven; de app beslist wat het merk doet.
   */
  wrapBrand?: (brand: ReactNode) => ReactNode;
}

/** Donkerblauwe zijbalk (style guide §Sidebar). Labels gebruiken --sidebar-label. */
export function Sidebar({
  brand,
  organisation,
  logoUrl,
  sections,
  activeKey,
  renderLink,
  footer,
  wrapBrand,
}: SidebarProps) {
  const brandBlock = (
    <div className="flex min-w-0 items-center gap-2">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          data-testid="sidebar-logo"
          className="h-8 w-8 shrink-0 rounded-sm object-contain"
        />
      ) : null}
      <p
        data-testid="sidebar-brand"
        className="min-w-0 truncate font-heading text-title-md font-bold tracking-tight"
      >
        {brand}
      </p>
    </div>
  );

  return (
    <nav
      aria-label="Hoofdnavigatie"
      className="flex w-60 shrink-0 flex-col gap-section bg-brand-sidebar px-4 py-6 text-text-on-dark"
    >
      <div className="px-2">
        <div className="flex items-center">{wrapBrand ? wrapBrand(brandBlock) : brandBlock}</div>
        {organisation ? (
          <p className="mt-1 text-caption text-[color:var(--sidebar-label)]">{organisation}</p>
        ) : null}
      </div>

      {sections.map((section, index) => (
        <div key={section.label ?? index} className="flex flex-col gap-1">
          {section.label ? (
            <p className="px-2 pb-1 font-heading text-micro font-semibold uppercase tracking-[0.08em] text-[color:var(--sidebar-label)]">
              {section.label}
            </p>
          ) : null}
          {section.items.map((item) =>
            renderLink(
              item,
              cn(
                'flex items-center gap-inline rounded-pill px-3 py-2 font-heading text-body font-medium transition-colors',
                item.key === activeKey
                  ? 'bg-[rgba(255,255,255,0.14)] text-text-on-dark'
                  : 'text-[color:var(--sidebar-label)] hover:bg-[rgba(255,255,255,0.08)] hover:text-text-on-dark',
              ),
              <>
                {item.icon}
                <span className="flex-1">{item.label}</span>
              </>,
            ),
          )}
        </div>
      ))}

      <div className="mt-auto">{footer}</div>
    </nav>
  );
}

export interface HeaderProps {
  title: string;
  userName: string;
  actions?: ReactNode;
  search?: ReactNode;
}

/** Paginaheader met zoekbalk links en profiel rechts (style guide §Header). */
export function Header({ title, userName, actions, search }: HeaderProps) {
  return (
    <header className="flex items-center gap-element border-b border-border bg-surface px-page-x py-4">
      <h1 className="font-heading text-page font-semibold text-text-primary">{title}</h1>
      <div className="ml-4 max-w-sm flex-1">{search}</div>
      <div className="ml-auto flex items-center gap-inline">
        {actions}
        <span className="flex items-center gap-inline">
          <Avatar name={userName} self size="sm" />
          <span className="hidden text-small text-text-secondary sm:inline">{userName}</span>
        </span>
      </div>
    </header>
  );
}

export interface ZoneShellProps {
  zone: Zone;
  sidebar?: ReactNode;
  header?: ReactNode;
  children: ReactNode;
}

/**
 * Zoneschil. `data-zone` stuurt de tokenset aan: de app-zone draait op de AA-set
 * met 16px basistekst en 48px raakvlakken (docs/design/a11y.md, ADR-0018).
 */
export function ZoneShell({ zone, sidebar, header, children }: ZoneShellProps) {
  return (
    <div data-zone={zone} className="flex min-h-screen bg-background">
      {sidebar}
      <div className="flex min-w-0 flex-1 flex-col">
        {header}
        <main className="flex-1 px-page-x py-page-y">{children}</main>
      </div>
    </div>
  );
}
